import { useState } from "react";
import {
  Button,
  Card,
  CountrySelect,
  DEFAULT_COUNTRY_CODE,
  Icon,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  useToast,
} from "@app/ui";
import { createPartner, deletePartner, listPartners, updatePartner } from "../api/dpp.js";
import type { Partner, PartnerBody } from "../api/dpp.js";
import { useAsync } from "../api/useAsync.js";
import { customCategoriesOf, PARTNER_CATEGORIES, usePartnerCategoryLabel } from "../dpp/partnerCategories.js";
import { ErrorAlert, useErrorText } from "../errors/index.js";
import { useT } from "../i18n.js";

const EMPTY_PARTNER = { name: "", category: "repair", countryIso: DEFAULT_COUNTRY_CODE, city: "", url: "" };

/** Circularity partners (repairers, recyclers, …) with their create/edit dialog. */
export function PartnersCard({ orgId, canWrite }: { orgId: string; canWrite: boolean }) {
  const t = useT();
  const categoryLabel = usePartnerCategoryLabel();
  const toast = useToast();
  const errorText = useErrorText();
  const { data: partners, error, loading, reload } = useAsync(() => listPartners(orgId), [orgId]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState(EMPTY_PARTNER);
  const [saving, setSaving] = useState(false);

  const openModal = (partner?: Partner) => {
    setEditId(partner?.id ?? null);
    setDraft(
      partner
        ? {
            name: partner.name,
            category: partner.category,
            countryIso: partner.countryIso ?? "",
            city: partner.city ?? "",
            url: partner.url ?? "",
          }
        : EMPTY_PARTNER,
    );
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body: PartnerBody = {
        name: draft.name.trim(),
        category: draft.category,
        ...(draft.countryIso.trim() ? { countryIso: draft.countryIso.trim() } : {}),
        ...(draft.city.trim() ? { city: draft.city.trim() } : {}),
        ...(draft.url.trim() ? { url: draft.url.trim() } : {}),
      };
      if (editId) await updatePartner(orgId, editId, body);
      else await createPartner(orgId, body);
      toast.success(t("partners.saved"));
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await deletePartner(orgId, id);
      toast.success(t("partners.deleted"));
      reload();
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  const list = partners ?? [];
  // Canonical categories + the ones this org has already created; the draft's
  // own value is kept listed so a freshly typed category stays selected.
  const categoryOptions = [
    ...PARTNER_CATEGORIES.map((c) => ({ label: t(`partnerCat.${c}`), value: c })),
    ...customCategoriesOf(list).map((c) => ({ label: c, value: c })),
    ...(draft.category &&
    !PARTNER_CATEGORIES.includes(draft.category as (typeof PARTNER_CATEGORIES)[number]) &&
    !list.some((p) => p.category === draft.category)
      ? [{ label: draft.category, value: draft.category }]
      : []),
  ];

  return (
    <Card
      title={t("partners.title")}
      extra={
        canWrite && (
          <Button variant="primary" icon={<Icon name="plus" size={16} />} onClick={() => openModal()}>
            {t("partners.new")}
          </Button>
        )
      }
    >
      {error != null ? (
        <ErrorAlert error={error} onRetry={reload} />
      ) : loading && !partners ? (
        <Spin />
      ) : (
        <Table
          rowKey={(p) => p.id}
          empty={t("partners.empty")}
          columns={[
            { key: "name", title: t("facilities.name"), render: (p) => p.name },
            { key: "category", title: t("partners.category"), render: (p) => categoryLabel(p.category) },
            {
              key: "location",
              title: t("facilities.location"),
              render: (p) => [p.city, p.countryIso].filter(Boolean).join(", ") || "—",
            },
            { key: "url", title: "URL", render: (p) => p.url ?? "—" },
            {
              key: "actions",
              title: t("common.actions"),
              render: (p) =>
                canWrite ? (
                  <Space>
                    <Button size="sm" onClick={() => openModal(p)}>
                      {t("common.edit")}
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => void remove(p.id)}>
                      {t("common.remove")}
                    </Button>
                  </Space>
                ) : null,
            },
          ]}
          data={list}
        />
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editId ? t("partners.edit") : t("partners.new")}
        onOk={() => void save()}
        okText={t("common.save")}
        cancelText={t("common.cancel")}
        confirmLoading={saving}
        width={520}
      >
        <Space direction="vertical" size="md" style={{ width: "100%" }}>
          <Input
            label={t("facilities.name")}
            required
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
          <Select
            label={t("partners.category")}
            help={t("partners.categoryHint")}
            creatable
            searchPlaceholder={t("partners.categorySearch")}
            createLabel={(name) => t("partners.categoryCreate", { name })}
            options={categoryOptions}
            value={draft.category}
            onChange={(v) => setDraft({ ...draft, category: v })}
          />
          <Space wrap>
            <div style={{ width: 240 }}>
              <CountrySelect
                label={t("facilities.country")}
                allowClear
                placeholder={t("facilities.countryPlaceholder")}
                value={draft.countryIso}
                onChange={(v) => setDraft({ ...draft, countryIso: v })}
              />
            </div>
            <Input
              label={t("facilities.city")}
              value={draft.city}
              onChange={(e) => setDraft({ ...draft, city: e.target.value })}
            />
          </Space>
          <Input
            label="URL"
            placeholder="https://…"
            value={draft.url}
            onChange={(e) => setDraft({ ...draft, url: e.target.value })}
          />
        </Space>
      </Modal>
    </Card>
  );
}
